import { Button, ButtonIcon, Inline, TrashIcon } from 'canoe';
import { Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import React from 'react';

import { FilterTypeSelector } from './FilterTypeSelector';
import { OperationOperandInput } from './OperationOperandInput';
import { OperationTypeSelector } from './OperationTypeSelector';
import { FieldWrapper, StyledForm } from './StyledForm';
import { TraitSelector } from './TraitSelector';
import { Filter, FilterOperations, FilterTypes, GenericFilter, UserPropertyFilter } from './types';
import { isUserPropertyFilter } from './utils';
import { validationSchema } from './validationSchema';

export const defaultValues: Filter = {
  id: '',
  type: FilterTypes.UserId,
  operation: {
    type: FilterOperations.Equals,
    operand: '',
  },
};

interface Props {
  onSave: (filter: GenericFilter | UserPropertyFilter) => void;
  availableFilters: FilterTypes[];
  filter?: Filter;
  onRemove?: () => void;
  onCancel?: () => void;
}

export const FilterForm = ({ onSave, onRemove, onCancel, filter, availableFilters }: Props) => {
  const onSubmit = (values: GenericFilter | UserPropertyFilter) => onSave(values);

  return (
    <Formik
      initialValues={filter || defaultValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (isUserPropertyFilter(values)) {
          onSubmit(values as UserPropertyFilter);
        } else {
          onSubmit(values as GenericFilter);
        }
      }}
    >
      {({ values, dirty, initialValues }) => {
        return (
          <StyledForm>
            <FieldWrapper maxWidth={200}>
              <FilterTypeSelector availableFilters={availableFilters} />
            </FieldWrapper>

            {values.type === FilterTypes.UserProperty && (
              <FieldWrapper>
                <TraitSelector />
              </FieldWrapper>
            )}

            <FieldWrapper maxWidth={200}>
              <OperationTypeSelector />
            </FieldWrapper>

            <FieldWrapper>
              <OperationOperandInput />
            </FieldWrapper>

            <Inline align="center" space="xs" wrap="nowrap">
              <Button
                variant="secondary"
                type="submit"
                disabled={!dirty || isEqual(values, initialValues)}
              >
                {filter ? 'Update' : 'Apply'}
              </Button>

              {onCancel && (
                <Button variant="secondary" appearance="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}

              {onRemove && <ButtonIcon color="contrast" icon={TrashIcon} onClick={onRemove} />}
            </Inline>
          </StyledForm>
        );
      }}
    </Formik>
  );
};
